import React from "react";
import PropTypes from "prop-types";
import { FormControl } from "react-bootstrap";
import ColorPicker from "../common/color-picker/ColorPicker";

// Form controls for the VectorShape sections in the right rail. They follow
// the rail's own vocabulary (Yapı / Araçlar): uppercase grey labels, the
// panel's range style with a small editable value box, grey segmented groups
// with a light-purple active state, and the standard ColorPicker /
// FormControl fields.

export const Section = ({ children }) => <div className="shape-settings__section">{children}</div>;

Section.propTypes = { children: PropTypes.node };

export const Field = ({ label, children, testId }) => (
  <div className="shape-settings__field" data-testid={testId}>
    {label ? <span className="shape-settings__label">{label}</span> : null}
    {children}
  </div>
);

Field.propTypes = { label: PropTypes.node, children: PropTypes.node, testId: PropTypes.string };

// One-of-many buttons. Options: { value, label?, Icon?, title? }.
export const Segmented = ({ value, options, onChange, testId }) => (
  <div className="shape-settings__segmented" role="radiogroup" data-testid={testId}>
    {options.map(({ value: v, label, Icon, title }) => (
      <button
        key={String(v)}
        type="button"
        role="radio"
        aria-checked={value === v}
        title={title || (typeof label === "string" ? label : undefined)}
        className={`shape-settings__segment${value === v ? " is-active" : ""}`}
        onClick={() => onChange(v)}
      >
        {Icon ? <Icon size={14} /> : null}
        {label ? <span>{label}</span> : null}
      </button>
    ))}
  </div>
);

Segmented.propTypes = {
  value: PropTypes.any,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const SliderRow = ({ label, value, min, max, step = 1, unit = "", onChange, testId }) => (
  <Field label={label}>
    <div className="shape-settings__slider-row">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="shape-settings__value">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-label={typeof label === "string" ? label : undefined}
          onChange={(e) => {
            if (e.target.value === "") return;
            onChange(clamp(Number(e.target.value), min, max));
          }}
        />
        {unit ? <span className="shape-settings__unit">{unit}</span> : null}
      </span>
    </div>
  </Field>
);

SliderRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  unit: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

// The editor's standard colour field (same ColorPicker the right rail uses
// for every "color" button): swatch + native picker, HEX and opacity %.
export const ColorField = ({ label, value, onChange, testId }) => (
  <Field label={label} testId={testId}>
    <ColorPicker value={value} onChange={({ color }) => onChange(color)} />
  </Field>
);

ColorField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

export const IconToggle = ({ active, title, onClick, children }) => (
  <button
    type="button"
    aria-pressed={!!active}
    title={title}
    className={`shape-settings__icon-toggle${active ? " is-active" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

IconToggle.propTypes = {
  active: PropTypes.bool,
  title: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export const SelectField = ({ label, value, onChange, children }) => (
  <Field label={label}>
    <FormControl
      as="select"
      className="select shape-settings__select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </FormControl>
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};
